// morning-ai.js — client calls to the SERVER-side Morning Mode AI endpoints.
//
// These are the only AI calls in the app that DON'T use the browser key: the
// server holds ANTHROPIC_API_KEY. We authenticate with the user's Clerk session
// token (Bearer), same pattern as src/lib/push.js. Callers pass Clerk's getToken
// (from useAuth()). Every function returns parsed JSON; callers own the fallback
// (the endpoints themselves degrade gracefully — vision returns partials, recipe
// gen returns the deterministic result — so a thrown error here means transport,
// not a dead end).

async function authFetch(path, body, getToken) {
  const token = getToken ? await getToken().catch(() => null) : null;
  const res = await fetch(path, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json?.error || `${path} failed (${res.status})`);
  return json;
}

// Read a File/Blob as a base64 data URL for the vision endpoint.
export function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

/**
 * Bag photo → structured bean fields. Returns
 * { roaster, name, origin[], process, roast_level, roast_date, label_weight_g, confidence, partial? }.
 * On partial/low-confidence the caller should drop to the manual form.
 */
export async function visionBagPhoto({ getToken, file, imageBase64, imageUrl }) {
  const body = imageUrl ? { imageUrl }
    : { imageBase64: imageBase64 || (file ? await fileToDataUrl(file) : null) };
  return authFetch("/api/vision", body, getToken);
}

// Map an /api/brew-recipe card → the brew object shape store.logBrew /
// store.logBlendBrew persist (dose_g, water_g, grind.setting, temp_c, profile.id,
// aidenProfile, source, rationale). Keeps all branches consistent.
export function recipeToBrew(recipe, bean) {
  const ratio = recipe?.params?.ratio || recipe?.aiden_profile?.ratio || 15;
  return {
    bean_id: bean?.id ?? null,
    dose_g: recipe?.dose_g,
    water_g: recipe?.dose_g != null ? Math.round(recipe.dose_g * ratio) : null,
    grind: { setting: recipe?.grind_setting },
    temp_c: recipe?.params?.temp_c,
    profile: { id: recipe?.aiden_profile?.id || "balanced" },
    aidenProfile: recipe?.aiden_profile,
    source: recipe?.source,
    rationale: recipe?.rationale,
  };
}

/**
 * Generate a recipe card. Returns
 * { dose_g, cups, grind_setting, aiden_profile, params:{temp_f,temp_c,ratio,bloom_s,...}, rationale, source }.
 * `descriptors` drives the deterministic tweak; `remnant`/`newBag` drive blends.
 */
export async function generateBrewRecipe({ getToken, bean, tasteProfile, mode = "hot", cups = 8, descriptors, remnant, newBag, blend_components, source, grinder = null }) {
  // The server has no access to the appliances table, so the grinder scale has
  // to travel with the request. Without it the route clamps every user to the
  // Fellow Ode's 1-11 range regardless of what they actually own.
  return authFetch("/api/brew-recipe", {
    bean, tasteProfile, mode, cups, descriptors, remnant, newBag, blend_components, source, grinder,
  }, getToken);
}
